import { AnimatePresence, motion } from "framer-motion";
import { DesigningScreen } from "./components/DesigningScreen";
import { GameOverScreen } from "./components/GameOverScreen";
import { FunniestScreen, RevealScreen, RoundEndScreen } from "./components/RevealScreens";
import { GuessingScreen } from "./components/GuessingScreen";
import { HomeScreen } from "./components/HomeScreen";
import { InvitePair } from "./components/InviteButton";
import { LobbyScreen } from "./components/LobbyScreen";
import { UrgencyLights } from "./components/UrgencyLights";
import { WritingScreen } from "./components/WritingScreen";
import { Button, ErrorBanner, Shell } from "./components/ui";
import { useGameSocket } from "./hooks/useGameSocket";
import { usePhaseCountdown } from "./hooks/usePhaseCountdown";

export default function App() {
  const game = useGameSocket();
  const { state, status, lastError, clearError } = game;
  const isHost = Boolean(state && state.hostId === state.you.id);
  const connecting = status === "connecting";
  const writingKind = state?.round?.kind ?? "text";
  const wideShell = Boolean(
    state?.phase === "writing" && writingKind === "design",
  );
  // Global urgency wash for timed phases (hide once you've submitted).
  const showUrgency =
    Boolean(state?.phaseEndsAt) &&
    Boolean(state?.round) &&
    !state?.round?.youSubmitted &&
    (state?.phase === "writing" ||
      state?.phase === "guessing" ||
      state?.phase === "funniest");
  const countdown = usePhaseCountdown(
    showUrgency ? state?.phaseEndsAt : undefined,
  );

  return (
    <Shell wide={wideShell}>
      <UrgencyLights urgency={showUrgency ? countdown.urgency : "none"} />
      {lastError && state && (
        <ErrorBanner message={lastError.message} onDismiss={clearError} />
      )}

      <AnimatePresence mode="wait">
        {!state ? (
          <motion.div
            key="home"
            className="flex flex-1 flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <HomeScreen
              onCreate={game.createRoom}
              onJoin={game.joinRoom}
              error={lastError?.message}
              onDismissError={clearError}
              connecting={connecting}
            />
            {status === "disconnected" && (
              <div className="mt-4 text-center">
                <Button variant="secondary" onClick={game.reconnect}>
                  Reconnect to last room
                </Button>
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div
            key={`${state.code}-${state.phase}-${state.round?.kind ?? "none"}`}
            className="flex flex-1 flex-col"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22 }}
          >
            {state.phase === "lobby" && (
              <LobbyScreen
                state={state}
                isHost={isHost}
                onStart={game.startGame}
                onSetContentMode={game.setContentMode}
                onLeave={game.leave}
              />
            )}
            {state.phase === "writing" && state.round?.kind === "design" && (
              <DesigningScreen
                state={state}
                onSubmit={game.submitDesign}
              />
            )}
            {state.phase === "writing" &&
              state.round &&
              state.round.kind !== "design" && (
                <WritingScreen state={state} onSubmit={game.submitAnswer} />
              )}
            {state.phase === "guessing" && state.round && (
              <GuessingScreen state={state} onSubmit={game.submitGuesses} />
            )}
            {state.phase === "reveal" && state.round && (
              <RevealScreen
                state={state}
                isHost={isHost}
                onContinue={game.nextRound}
              />
            )}
            {state.phase === "funniest" && state.round && (
              <FunniestScreen
                state={state}
                isHost={isHost}
                onVote={game.voteFunniest}
                onSkip={game.nextRound}
              />
            )}
            {state.phase === "round_end" && state.round && (
              <RoundEndScreen
                state={state}
                isHost={isHost}
                onNext={game.nextRound}
                onEndGame={game.endGame}
                onLobby={game.returnToLobby}
              />
            )}
            {state.phase === "game_over" && (
              <GameOverScreen
                state={state}
                isHost={isHost}
                onLobby={game.returnToLobby}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {state && (
        <footer className="mt-6 flex flex-wrap items-center justify-between gap-3 text-[11px] text-[var(--color-muted)]">
          <span className="min-w-0">
            Room <span className="font-mono tracking-wider">{state.code}</span>
            <span className="mx-2 opacity-40">·</span>
            <span className="capitalize">{status}</span>
            {state.contentMode && state.contentMode !== "text" && (
              <>
                <span className="mx-2 opacity-40">·</span>
                <span className="capitalize">{state.contentMode}</span>
              </>
            )}
          </span>
          <div className="flex shrink-0 items-center gap-1">
            <InvitePair
              code={state.code}
              variant="ghost"
              compact
              className="flex items-center gap-1"
              buttonClassName="!px-2 !py-1 text-[11px] uppercase tracking-wide"
            />
            <button
              type="button"
              onClick={game.leave}
              className="rounded-lg px-2 py-1 font-medium tracking-wide text-[var(--color-muted)] uppercase transition hover:bg-white/5 hover:text-[var(--color-cream)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]"
              title="Leave room and return home (clears session so refresh won't rejoin)"
            >
              Leave game
            </button>
          </div>
        </footer>
      )}
    </Shell>
  );
}
